/**
 * Layer drawing.
 *
 * Tier 1 of the renderer lives in `buildDeviceRender`: clip a rounded rect at the frame's
 * screen area, draw the user's screenshot into it, draw the transparent bezel on top. That
 * is the whole core product, and it is why the default path needs no GPU and no 3D.
 *
 * Tier 2 (a leaning device) is Tier 1 plus a homography warp of the finished device render,
 * which is why `perspective` is handled here rather than in a separate 3D path.
 */
import type { DeviceFrame } from '@keymaker/frames';
import type {
  Background,
  CanvasSize,
  DeviceLayer,
  ElementLayer,
  ImageLayer,
  Layer,
  ShapeLayer,
} from '@keymaker/schema';
import type { Ctx2D, RenderEnvironment, RenderImage, RenderOptions } from './env';
import { RenderError } from './env';
import { coverRect, containRect, resolveBox, resolveDeviceRect, type Rect } from './fit';
import { projectedQuad, warpImage } from './perspective';
import { drawText, layoutText } from './text';
import { elementById } from './elements';

export interface DeviceRender {
  /** Drawable device render: a Tier 1 composite, or a pre-rendered Tier 3 body. */
  image: RenderImage;
  /** Where the screenshot landed inside the frame canvas, in frame pixels. */
  screenRect: Rect;
  /**
   * `|screenAspect / captureAspect - 1|`, or null when they match.
   *
   * Reported rather than corrected: silently stretching a capture to hide a mismatch
   * produces a screenshot that looks right and is subtly wrong, which is worse than a
   * number in the render log.
   */
  aspectMismatch: number | null;
}

export interface DrawContext {
  env: RenderEnvironment;
  canvas: CanvasSize;
  options: RenderOptions;
  frames: readonly DeviceFrame[];
}

function requireAsset(ctx: DrawContext, ref: string, where: string): RenderImage | null {
  const image = ctx.options.assets?.get(ref);
  if (!image) {
    if (ctx.options.onMissingAsset) {
      ctx.options.onMissingAsset(ref, where);
      return null;
    }
    throw new RenderError(`Missing asset "${ref}" required by ${where}.`, 'missing-asset');
  }
  return image;
}

function findFrame(ctx: DrawContext, id: string): DeviceFrame {
  const frame = ctx.frames.find((f) => f.id === id);
  if (!frame) {
    throw new RenderError(
      `Unknown frame "${id}". The frame library has: ${ctx.frames.map((f) => f.id).join(', ')}.`,
      'unknown-frame',
    );
  }
  return frame;
}

/**
 * Composite a screenshot into a device frame, producing one device render.
 *
 * Callers should reuse the result across panels that share a frame and variant: the frame
 * asset decode and the bezel draw are the same every time, only the screen differs.
 */
export function buildDeviceRender(
  env: RenderEnvironment,
  frame: DeviceFrame,
  variantAsset: RenderImage,
  screenImage: RenderImage,
  screenFit: 'cover' | 'contain' = 'cover',
): DeviceRender {
  const canvas = env.createCanvas(frame.canvas.width, frame.canvas.height);
  const ctx = canvas.ctx;
  const { screen } = frame;

  ctx.save();
  ctx.beginPath();
  roundedRectPath(ctx, screen.x, screen.y, screen.width, screen.height, screen.radius);
  ctx.clip();

  const target: Rect = { x: screen.x, y: screen.y, width: screen.width, height: screen.height };
  const placed =
    screenFit === 'cover'
      ? coverRect({ width: screenImage.width, height: screenImage.height }, target)
      : containRect({ width: screenImage.width, height: screenImage.height }, target);

  env.drawImage(ctx, screenImage, placed.x, placed.y, placed.width, placed.height);
  ctx.restore();

  env.drawImage(ctx, variantAsset, 0, 0);

  const captureAspect = screenImage.width / screenImage.height;
  const screenAspect = screen.width / screen.height;
  const mismatch = Math.abs(screenAspect / captureAspect - 1);

  return {
    image: canvas,
    screenRect: target,
    aspectMismatch: mismatch < 0.005 ? null : Number(mismatch.toFixed(4)),
  };
}

export function roundedRectPath(
  ctx: Ctx2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  const r = Math.max(0, Math.min(radius, Math.min(width, height) / 2));
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.arcTo(x + width, y, x + width, y + r, r);
  ctx.lineTo(x + width, y + height - r);
  ctx.arcTo(x + width, y + height, x + width - r, y + height, r);
  ctx.lineTo(x + r, y + height);
  ctx.arcTo(x, y + height, x, y + height - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}

function variantImageFor(ctx: DrawContext, frame: DeviceFrame, variant?: string): RenderImage | null {
  const chosen = variant
    ? frame.variants.find((v) => v.key === variant)
    : (frame.variants.find((v) => v.asset === frame.asset) ?? frame.variants[0]);

  if (!chosen) {
    throw new RenderError(
      `Frame "${frame.id}" has no variant${variant ? ` "${variant}"` : ''}. Known: ${frame.variants
        .map((v) => v.key)
        .join(', ')}.`,
      'unknown-variant',
    );
  }

  // Variants are asset keys in the frame library's own space; the caller's asset map is
  // keyed the same way by convention.
  const image = ctx.options.assets?.get(chosen.asset) ?? ctx.options.assets?.get(chosen.key);
  if (image) return image;

  // A missing *asset* is a load-order problem worth degrading for (the editor previews while
  // images stream in); a missing *variant name* is a template bug and still throws above.
  if (ctx.options.onMissingAsset) {
    ctx.options.onMissingAsset(chosen.asset, `frame "${frame.id}" variant "${chosen.key}"`);
    return null;
  }
  throw new RenderError(
    `Frame "${frame.id}" variant "${chosen.key}" needs asset "${chosen.asset}" in the asset map.`,
    'missing-asset',
  );
}

/** Draw one device layer, including the optional Tier 2 perspective warp. */
export function drawDeviceLayer(ctx2d: Ctx2D, layer: DeviceLayer, ctx: DrawContext): DeviceRender {
  const frame = findFrame(ctx, layer.frame);
  const screenImage = requireAsset(ctx, layer.screen, `device layer "${layer.id}"`);

  const empty: DeviceRender = {
    image: ctx.env.createCanvas(frame.canvas.width, frame.canvas.height),
    screenRect: { x: 0, y: 0, width: 0, height: 0 },
    aspectMismatch: null,
  };

  // Every reference is resolved before deciding to bail, so a caller missing several assets hears
  // about all of them in one pass instead of fixing one and rediscovering the next.
  const variant = layer.kind === '3d' ? undefined : variantImageFor(ctx, frame, layer.variant);
  const variantMissing = layer.kind !== '3d' && variant === null;
  if (screenImage === null || variantMissing) return empty;

  let render: DeviceRender;
  if (layer.kind === '3d') {
    // Tier 3 arrives pre-rendered: building a body needs WebGL, which is asynchronous, and making
    // the compositing path async for a feature most projects never use would be the wrong trade.
    const preRendered = ctx.options.devices?.get(layer.id);
    if (!preRendered) {
      throw new RenderError(
        `Device layer "${layer.id}" is kind "3d" but no body was supplied. ` +
          (ctx.env.render3d
            ? 'Call prepare3dDevices() and pass the result as `devices`.'
            : 'This environment provides no render3d, so Tier 3 is unavailable here; use kind "frame".'),
        '3d-unavailable',
      );
    }
    // The tilt was applied as real 3D rotation during the pre-pass, so no homography here.
    render = { image: preRendered, screenRect: { ...frame.screen }, aspectMismatch: null };
  } else {
    render = buildDeviceRender(ctx.env, frame, variant as RenderImage, screenImage, layer.screenFit);
  }

  const rect = resolveDeviceRect(layer, ctx.canvas, frame.canvas);

  ctx2d.save();
  ctx2d.globalAlpha = layer.opacity;

  if (layer.shadow) {
    const blur = Math.max(layer.shadow.blur * ctx.canvas.width, 0);
    const offsetY = layer.shadow.offsetY * ctx.canvas.height;
    ctx2d.save();
    // `filter` is honoured by browsers and Skia; where it is not, the shadow degrades to a
    // hard-edged copy rather than failing the render.
    const withFilter = ctx2d as unknown as { filter: string };
    const supportsFilter = typeof withFilter.filter === 'string';
    if (supportsFilter) withFilter.filter = `blur(${blur.toFixed(1)}px)`;
    ctx2d.globalAlpha = layer.opacity * layer.shadow.opacity;
    ctx2d.fillStyle = layer.shadow.color;
    // A silhouette rather than a rectangle, so the shadow follows the device rather than
    // announcing itself as a box behind one.
    ctx2d.beginPath();
    roundedRectPath(
      ctx2d,
      rect.x,
      rect.y + offsetY,
      rect.width,
      rect.height,
      (frame.screen.radius / frame.canvas.width) * rect.width * 1.12,
    );
    ctx2d.fill();
    if (supportsFilter) withFilter.filter = 'none';
    ctx2d.restore();
  }

  if (
    layer.kind !== '3d' &&
    layer.perspective &&
    (layer.perspective.tiltX !== 0 || layer.perspective.tiltY !== 0)
  ) {
    const quad = projectedQuad(rect, {
      tiltX: layer.perspective.tiltX,
      tiltY: layer.perspective.tiltY,
      perspective: layer.perspective.perspective,
    });
    warpImage(
      ctx2d,
      ctx.env,
      render.image,
      { x: 0, y: 0, width: render.image.width, height: render.image.height },
      quad,
      { steps: Math.max(16, Math.round(Math.max(rect.width, rect.height) / 40)) },
    );
  } else {
    ctx2d.save();
    if (layer.transform.rotation !== 0) {
      const anchorX = rect.x + rect.width / 2;
      const anchorY = rect.y + rect.height / 2;
      ctx2d.translate(anchorX, anchorY);
      ctx2d.rotate((layer.transform.rotation * Math.PI) / 180);
      ctx2d.translate(-anchorX, -anchorY);
    }
    ctx.env.drawImage(ctx2d, render.image, rect.x, rect.y, rect.width, rect.height);
    ctx2d.restore();
  }

  ctx2d.restore();
  return render;
}

export function drawBackground(ctx2d: Ctx2D, background: Background, ctx: DrawContext): void {
  const { width, height } = ctx.canvas;
  switch (background.type) {
    case 'none':
      return;
    case 'solid':
      ctx2d.fillStyle = background.color;
      ctx2d.fillRect(0, 0, width, height);
      return;
    case 'gradient': {
      // 0 degrees runs top→bottom, 90 runs left→right.
      const rad = (background.angle * Math.PI) / 180;
      const dx = Math.sin(rad);
      const dy = Math.cos(rad);
      const cx = width / 2;
      const cy = height / 2;
      const length = Math.abs(width * dx) + Math.abs(height * dy);
      const gradient = ctx2d.createLinearGradient(
        cx - (dx * length) / 2,
        cy - (dy * length) / 2,
        cx + (dx * length) / 2,
        cy + (dy * length) / 2,
      );
      const sorted = [...background.stops].sort((a, b) => a.offset - b.offset);
      for (const stop of sorted) gradient.addColorStop(stop.offset, stop.color);
      ctx2d.fillStyle = gradient;
      ctx2d.fillRect(0, 0, width, height);
      return;
    }
    case 'radial': {
      const cx = background.center.x * width;
      const cy = background.center.y * height;
      const radius = background.radius * height;
      const gradient = ctx2d.createRadialGradient(cx, cy, 0, cx, cy, radius);
      const sorted = [...background.stops].sort((a, b) => a.offset - b.offset);
      for (const stop of sorted) gradient.addColorStop(stop.offset, stop.color);
      ctx2d.fillStyle = gradient;
      ctx2d.fillRect(0, 0, width, height);
      return;
    }
    case 'image': {
      const image = requireAsset(ctx, background.src, 'background');
      if (!image) return;
      const target: Rect = { x: 0, y: 0, width, height };
      if (background.fit === 'tile') {
        for (let y = 0; y < height; y += image.height) {
          for (let x = 0; x < width; x += image.width) {
            ctx.env.drawImage(ctx2d, image, x, y);
          }
        }
        return;
      }
      const placed =
        background.fit === 'cover'
          ? coverRect({ width: image.width, height: image.height }, target)
          : containRect({ width: image.width, height: image.height }, target);
      ctx.env.drawImage(ctx2d, image, placed.x, placed.y, placed.width, placed.height);
      return;
    }
  }
}

export function drawShapeLayer(ctx2d: Ctx2D, layer: ShapeLayer, ctx: DrawContext): void {
  const rect = resolveBox(layer.box, ctx.canvas);
  ctx2d.save();
  ctx2d.globalAlpha = layer.opacity;

  if (layer.rotation !== 0) {
    const cx = rect.x + rect.width / 2;
    const cy = rect.y + rect.height / 2;
    ctx2d.translate(cx, cy);
    ctx2d.rotate((layer.rotation * Math.PI) / 180);
    ctx2d.translate(-cx, -cy);
  }

  ctx2d.beginPath();
  switch (layer.kind) {
    case 'rect':
      ctx2d.rect(rect.x, rect.y, rect.width, rect.height);
      break;
    case 'rounded-rect':
      roundedRectPath(ctx2d, rect.x, rect.y, rect.width, rect.height, layer.radius * rect.height);
      break;
    case 'ellipse':
      ctx2d.ellipse(
        rect.x + rect.width / 2,
        rect.y + rect.height / 2,
        rect.width / 2,
        rect.height / 2,
        0,
        0,
        Math.PI * 2,
      );
      break;
    case 'line':
      ctx2d.moveTo(rect.x, rect.y + rect.height / 2);
      ctx2d.lineTo(rect.x + rect.width, rect.y + rect.height / 2);
      break;
  }

  if (layer.fill) {
    ctx2d.fillStyle = layer.fill;
    ctx2d.fill();
  }
  if (layer.stroke && layer.strokeWidth > 0) {
    ctx2d.strokeStyle = layer.stroke;
    ctx2d.lineWidth = layer.strokeWidth * ctx.canvas.width;
    ctx2d.stroke();
  }
  ctx2d.restore();
}

/**
 * Draw a ready-made element.
 *
 * The catalogue's shapes are drawn in a **unit box** — `1` is the layer's width and `1` is its height —
 * so the context is scaled into it and no definition has to know the canvas size or the family. That is
 * also what makes an element scale and recolour like any other layer instead of being a picture.
 *
 * Fills only, deliberately: scaling a context non-uniformly stretches a stroke's width along one axis,
 * so an element that wants an outline draws it as a filled area instead. The same function draws in the
 * editor, in the browser export and on the server, so an element cannot look like one thing while it is
 * being edited and another in the file.
 *
 * Returns a warning rather than throwing, so one unknown id is a message and not a failed render.
 */
export function drawElementLayer(ctx2d: Ctx2D, layer: ElementLayer, ctx: DrawContext): string | null {
  const definition = elementById(layer.element);
  if (!definition) {
    return `Element layer "${layer.id}" names unknown element "${layer.element}".`;
  }

  const rect = resolveBox(layer.box, ctx.canvas);
  ctx2d.save();
  ctx2d.globalAlpha = layer.opacity;

  if (layer.rotation !== 0) {
    const cx = rect.x + rect.width / 2;
    const cy = rect.y + rect.height / 2;
    ctx2d.translate(cx, cy);
    ctx2d.rotate((layer.rotation * Math.PI) / 180);
    ctx2d.translate(-cx, -cy);
  }

  ctx2d.save();
  ctx2d.translate(rect.x, rect.y);
  // A zero-width box would collapse the unit space and take the shape with it.
  ctx2d.scale(Math.max(rect.width, 0.0001), Math.max(rect.height, 0.0001));
  ctx2d.fillStyle = layer.fill;
  // The box's own proportions go in: the unit space is stretched to the box, and anything that has to
  // stay round divides its horizontal radii by this.
  definition.draw(ctx2d, layer, rect.width / Math.max(rect.height, 0.0001));
  ctx2d.restore();

  // The label is drawn after the scale is undone, in canvas units: the type must not be stretched with
  // the box, and its size is the fraction of canvas height the layer asked for.
  if (layer.label && definition.labelBox) {
    const box = definition.labelBox;
    ctx2d.fillStyle = layer.labelColor ?? layer.fill;
    ctx2d.font = `600 ${Math.round(layer.labelSize * ctx.canvas.height)}px Inter, "Helvetica Neue", Arial, sans-serif`;
    ctx2d.textAlign = 'center';
    ctx2d.textBaseline = 'middle';
    ctx2d.fillText(
      layer.label,
      rect.x + (box.x + box.width / 2) * rect.width,
      rect.y + (box.y + box.height / 2) * rect.height,
    );
  }
  ctx2d.restore();
  return null;
}

export function drawImageLayer(ctx2d: Ctx2D, layer: ImageLayer, ctx: DrawContext): void {
  const image = requireAsset(ctx, layer.src, `image layer "${layer.id}"`);
  if (!image) return;
  const rect = resolveBox(layer.box, ctx.canvas);

  ctx2d.save();
  ctx2d.globalAlpha = layer.opacity;
  if (layer.rotation !== 0) {
    const cx = rect.x + rect.width / 2;
    const cy = rect.y + rect.height / 2;
    ctx2d.translate(cx, cy);
    ctx2d.rotate((layer.rotation * Math.PI) / 180);
    ctx2d.translate(-cx, -cy);
  }

  if (layer.fit === 'stretch') {
    ctx.env.drawImage(ctx2d, image, rect.x, rect.y, rect.width, rect.height);
  } else if (layer.fit === 'tile') {
    ctx2d.save();
    ctx2d.beginPath();
    ctx2d.rect(rect.x, rect.y, rect.width, rect.height);
    ctx2d.clip();
    for (let y = rect.y; y < rect.y + rect.height; y += image.height) {
      for (let x = rect.x; x < rect.x + rect.width; x += image.width) {
        ctx.env.drawImage(ctx2d, image, x, y);
      }
    }
    ctx2d.restore();
  } else {
    const placed =
      layer.fit === 'cover'
        ? coverRect({ width: image.width, height: image.height }, rect)
        : containRect({ width: image.width, height: image.height }, rect);
    ctx.env.drawImage(ctx2d, image, placed.x, placed.y, placed.width, placed.height);
  }
  ctx2d.restore();
}

export function drawTextLayer(ctx2d: Ctx2D, layer: Extract<Layer, { type: 'text' }>, ctx: DrawContext): void {
  ctx2d.save();
  ctx2d.globalAlpha = layer.opacity;
  const layout = layoutText(ctx2d, layer, ctx.canvas);
  drawText(ctx2d, layer, layout, ctx.canvas);
  ctx2d.restore();
}
